// Modified by Antigravity - 2026-05-25

import TemplateDto from '@/services/skills/Dto/TemplateDto';
import PageTemplateDto from '@/services/skills/Dto/PageTemplateDto';
import TemplateRequestDto from '@/services/skills/Dto/TemplateRequestDto';
import TemplatesApiRepository from '@/services/skills/TemplatesApiRepository';
import AgentSettingsService from '@/services/account/agentSettingsService';
import { v4 as uuidv4 } from 'uuid';
import { isTemplateInBusinessAccount } from '@/utils/templateBusinessAccount';

const FLOW_TYPE = 'REQUEST_REVIEW';
const REVIEW_REQUEST_SKILL_ID = 'd7c0de0d-a4dc-4364-b32a-18b97b56adab';
const CATEGORY = 'MARKETING';

const state = () => ({
    templatesList: null,
    currentLanguage: 'en',
    currentTemplate: null,
    availableLanguages: [],
    skillConfig: {
        time_hours: 48,
        tags_exclude: 'lx-no-review',
        review_url: '',
        tag_review: 'lixsa_review_request_sent',
        smart_review_action_uuid: null,
    },
});

/**
 * Formats a language string to standard format (e.g., 'en_US').
 * @param {string} language - The language code to format.
 * @returns {string} The formatted language code.
 */
const formatLanguage = (language) => {
    if (!language) return '';
    const [base, region] = language.split('_');
    return region ? `${base.toLowerCase()}_${region.toUpperCase()}` : base.toLowerCase();
};

const getters = {
    getCurrentTemplate: (state) => state.currentTemplate,
    getCurrentLanguage: (state) => state.currentLanguage,
    getAvailableLanguages: (state) => state.availableLanguages,
    getSkillConfig: (state) => state.skillConfig,
    currentTemplateIsSavedInTemplatesList: (state) => {
        if (!state.currentTemplate) return false;

        return state.templatesList.content.some((template) => template.uuid === state.currentTemplate.uuid);
    },
    getCurrentTemplateStatus: (state) => {
        if (!state.currentTemplate) return null;

        return state.currentTemplate.status || 'NEW';
    },
};

const mutations = {
    SET_TEMPLATE_LIST(state, templatesList) {
        state.templatesList = templatesList;
    },
    SET_CURRENT_LANGUAGE(state, language) {
        state.currentLanguage = language;
    },
    SET_AVAILABLE_LANGUAGES(state, languages) {
        state.availableLanguages = languages;
    },
    SET_CURRENT_TEMPLATE(state, template) {
        state.currentTemplate = template;
    },
    SET_SKILL_CONFIG(state, config) {
        state.skillConfig = config;
    },
};

const actions = {
    async fetchTemplatesList({ commit }) {
        try {
            const templates = await TemplatesApiRepository.getTemplates(FLOW_TYPE);
            commit('SET_TEMPLATE_LIST', templates);
            return templates;
        } catch (error) {
            console.error('Error fetching templates:', error);
            throw error;
        }
    },

    async fetchCurrentTemplate({ commit, state, dispatch }) {
        const foundTemplate = state.templatesList.content.find(
            (template) => formatLanguage(template.language) === formatLanguage(state.currentLanguage),
        );
        if (foundTemplate) {
            const existingTemplate = new TemplateDto(foundTemplate);
            commit('SET_CURRENT_TEMPLATE', existingTemplate);
            return;
        }

        try {
            await dispatch('fetchDefaultTemplate');
        } catch (error) {
            console.error('Error fetching current template:', error);
        }
    },

    async fetchDefaultTemplate({ commit, state }) {
        try {
            const templateVariables = await TemplatesApiRepository.getTemplateVariables(
                REVIEW_REQUEST_SKILL_ID,
                FLOW_TYPE,
                state.currentLanguage,
            );
            const builder = new TemplateDto.Builder()
                .withUuid(templateVariables.uuid)
                .withTemplateType(templateVariables.templateType)
                .withFlowType(templateVariables.flowType)
                .withSkillId(templateVariables.skillId)
                .withBody(templateVariables.contentExample)
                .withLanguage(templateVariables.language)
                .withDateCreated(templateVariables.dateCreated);

            builder.variables = [templateVariables];
            const defaultTemplate = builder.build();

            commit('SET_CURRENT_TEMPLATE', defaultTemplate);
            return defaultTemplate;
        } catch (error) {
            console.error('Error fetching default template:', error);
            throw error;
        }
    },

    async fetchAvailableLanguages({ commit }) {
        try {
            const languages = await TemplatesApiRepository.getAvailableLanguages();
            commit('SET_AVAILABLE_LANGUAGES', languages);
        } catch (error) {
            console.error('Error fetching available languages:', error);
            throw error;
        }
    },

    async fetchCurrentLanguage({ rootGetters, state, commit }) {
        const hasShopifySkill = rootGetters['agentSettings/hasShopifySkill'];
        if (!hasShopifySkill) {
            commit('SET_CURRENT_LANGUAGE', 'en');
            return;
        }

        const shopifySkill = rootGetters['agentSettings/getSkillByInternalName']('Shopify');
        const shopifyLanguage = shopifySkill?.config_values?.countryCode;

        const language = state.availableLanguages.find((language) => language.tag === shopifyLanguage);

        commit('SET_CURRENT_LANGUAGE', language ? language.tag : 'en');
    },

    async updateTemplate(
        // eslint-disable-next-line no-unused-vars
        { commit, state },
        { templateContent, accountId, businessAccountId, defaultBusinessAccountId },
    ) {
        try {
            // Con varias líneas de WhatsApp hay una plantilla por idioma y WABA.
            const foundTemplate = state.templatesList.content.find(
                (template) =>
                    formatLanguage(template.language) === formatLanguage(state.currentLanguage) &&
                    isTemplateInBusinessAccount(template, businessAccountId, defaultBusinessAccountId),
            );
            if (foundTemplate) {
                // Update template
                const templateRequest = new TemplateRequestDto(foundTemplate);
                templateRequest.body = templateContent;
                await TemplatesApiRepository.updateTemplate(foundTemplate.uuid, templateRequest);
            } else {
                // Create template
                const templateRequest = new TemplateRequestDto.Builder()
                    .withUuid(uuidv4())
                    .withAccountId(accountId)
                    .withBody(templateContent)
                    .withLanguage(formatLanguage(state.currentLanguage))
                    .withTemplateName('rr')
                    .withCategory(CATEGORY)
                    .withType('TEXT')
                    .withTemplateType('FLOW')
                    .withFlowType(FLOW_TYPE)
                    .withSkillId(REVIEW_REQUEST_SKILL_ID)
                    .withChannelType('WHATSAPP')
                    .withBusinessAccountId(businessAccountId)
                    .build();
                await TemplatesApiRepository.createTemplate(templateRequest);
            }
        } catch (error) {
            console.error('Error updating template:', error);
            throw error;
        }
    },

    async deleteTemplate({ commit, state }, templateUuid) {
        try {
            const foundTemplate = state.templatesList.content.find((template) => template.uuid === templateUuid);
            if (foundTemplate) {
                const templateRequest = new TemplateRequestDto(foundTemplate);
                await TemplatesApiRepository.deleteTemplate(templateUuid, templateRequest);

                const updatedContent = state.templatesList.content.filter((t) => t.uuid !== templateUuid);
                const updatedList = new PageTemplateDto.Builder()
                    .withTotalElements(state.templatesList.totalElements - 1)
                    .withTotalPages(state.templatesList.totalPages)
                    .withFirst(state.templatesList.first)
                    .withLast(state.templatesList.last)
                    .withNumberOfElements(updatedContent.length)
                    .withSize(state.templatesList.size)
                    .withContent(updatedContent)
                    .withNumber(state.templatesList.number)
                    .withEmpty(updatedContent.length === 0)
                    .build();

                commit('SET_TEMPLATE_LIST', updatedList);
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            throw error;
        }
    },

    setCurrentLanguage({ commit }, language) {
        commit('SET_CURRENT_LANGUAGE', formatLanguage(language));
    },

    async subscribeToTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        try {
            await dispatch(
                'websocket/subscribe',
                { topic, callback: (data) => dispatch('handleTemplateEvent', data) },
                { root: true },
            );
        } catch (error) {
            console.error(`Failed to subscribe to ${topic}:`, error);
        }
    },

    unsubscribeFromTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        dispatch('websocket/unsubscribe', { topic }, { root: true });
    },

    handleTemplateEvent({ commit, state, dispatch }, event) {
        if (!event || !event.data) return;

        const templateData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        const template = new TemplateDto.Builder()
            .withUuid(templateData.uuid)
            .withAccountId(templateData.accountId)
            .withBody(templateData.body)
            .withHeader(templateData.header)
            .withChannelType(templateData.channelType)
            .withTemplateName(templateData.templateName)
            .withLanguage(templateData.language)
            .withCategory(templateData.category)
            .withStatus(templateData.status)
            .withScore(templateData.score)
            .withType(templateData.type)
            .withTemplateType(templateData.templateType)
            .withFlowType(templateData.flowType)
            .withSkillId(templateData.skillId)
            .withInternalTemplateId(templateData.internalTemplateId)
            .withTemplateParameters(templateData.templateParameters)
            .withExtraParameters(templateData.extraParameters)
            .withIssues(templateData.issues)
            .withModifyBy(templateData.modifyBy)
            .withDateCreated(templateData.dateCreated)
            .withVariables(templateData.variables || [])
            .build();

        const updatedTemplatesList = new PageTemplateDto(state.templatesList);
        const templateIndex = updatedTemplatesList.content.findIndex((t) => t.uuid === template.uuid);
        switch (event.name) {
            case 'TemplateUpdated':
                if (templateIndex !== -1) {
                    updatedTemplatesList.content[templateIndex] = template;
                    commit('SET_TEMPLATE_LIST', updatedTemplatesList);

                    if (state.currentTemplate?.uuid === template.uuid) {
                        dispatch('fetchCurrentTemplate');
                    }
                }
                break;
            default:
                console.warn(`Unhandled template event: ${event.name}`);
        }
    },

    /**
     * Fetches the review request skill configuration from agent settings.
     */
    async fetchSkillConfig({ commit }) {
        try {
            const response = await AgentSettingsService.getAgentSettings();
            const skills = response.data?.skills || [];
            const reviewSkill = skills.find((s) => s.skill_id === REVIEW_REQUEST_SKILL_ID);
            if (reviewSkill?.config_values) {
                commit('SET_SKILL_CONFIG', {
                    time_hours: reviewSkill.config_values.time_hours || 48,
                    tags_exclude: reviewSkill.config_values.tags_exclude || 'lx-no-review',
                    review_url: reviewSkill.config_values.review_url || '',
                    tag_review: reviewSkill.config_values.tag_review || 'lixsa_review_request_sent',
                    smart_review_action_uuid: reviewSkill.config_values.smart_review_action_uuid || null,
                });
            }
        } catch (error) {
            console.error('Error fetching review request config:', error);
        }
    },

    /**
     * Saves the review request skill configuration to agent settings.
     */
    async saveSkillConfig({ state }) {
        try {
            await AgentSettingsService.modifyAgentSettings(state.skillConfig, REVIEW_REQUEST_SKILL_ID);
        } catch (error) {
            console.error('Error saving review request config:', error);
            throw error;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
